import React, { useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Modal,
  PageHeader,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useNavigate } from 'react-router-dom';
import { MenuOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { arrayMoveImmutable } from 'array-move';
import type { SortableContainerProps, SortEnd } from 'react-sortable-hoc';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

import {
  useCancelAuctionMutation,
  useGetActiveAuctionQuery,
  useUpdateAuctionMutation,
  useUpdateAuctionVehiclesMutation,
} from '../../../app/api/auctionApiSlice';
import Vehicle from '../../../core/models/Vehicle';
import { formatDate, formatTime } from '../../../utils/dateTimeUtil';
import AddVehicles from '../components/AddVehicles';
import VehicleStatus from '../../../core/enum/VehicleStatus';
import VehicleStatusColorCode from '../../../core/enum/VehicleStatusColorCode';
import CreateAuction from '../components/CreateAuction';
import Spinner from '../../../components/common/Spinner';
import { getVehicleColors } from '../../../app/store/commonSlice';
import { useSelector } from 'react-redux';
import moment, { Moment } from 'moment';
import { RangePickerProps } from 'antd/lib/date-picker';

const NextAuction: React.FC = () => {
  const navigate = useNavigate();

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const vehicleColors = useSelector(getVehicleColors);

  const { data: auction, isLoading } = useGetActiveAuctionQuery();

  const [updateAuction, { isLoading: isUpdateAuctionLoading }] = useUpdateAuctionMutation();
  const [cancelAuction, { isLoading: isCancelAuctionLoading }] = useCancelAuctionMutation();
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);

  const [auctionDateTime, setAuctionDateTime] = useState<Moment | null>();

  const [updateAuctionVehicles, { isLoading: isUpdatingVehicles }] =
    useUpdateAuctionVehiclesMutation();

  const DragHandle = SortableHandle(() => (
    <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />
  ));

  const columns: ColumnsType<Vehicle> = [
    {
      title: 'Sort',
      dataIndex: 'sort',
      width: 30,
      className: 'drag-visible',
      render: () => <DragHandle />,
    },
    {
      title: 'Lot #',
      dataIndex: 'index',
      key: 'index',
      render: (value, item, index) => index + 1,
    },
    {
      title: 'VID',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Description',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Make',
      dataIndex: 'make_Text',
      key: 'make',
    },
    {
      title: 'Model',
      dataIndex: 'model_Text',
      key: 'model',
    },
    {
      title: 'YOM',
      dataIndex: 'yom',
      key: 'yom',
    },
    {
      title: 'Mileage',
      dataIndex: 'mileage',
      key: 'mileage',
      sorter: (a, b) => a.mileage - b.mileage,
      render: (mileage: number) => <>{mileage?.toLocaleString() || '-'} km</>,
      onFilter: (value, record) => {
        return record.mileage.toString().toLowerCase().includes(value.toString().toLowerCase());
      },
      responsive: ['xl'],
    },
    {
      title: 'Colour',
      dataIndex: 'color',
      key: 'colour',
      render: (value: number) => vehicleColors?.find((color) => color.id === value)?.name,
      sorter: (a, b) => a.color - b.color,
      filters: vehicleColors?.map((color) => ({ text: color.name, value: color.id })),
      filterSearch: true,
      onFilter: (value, record) => {
        return record.color === value;
      },
      responsive: ['xl'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: VehicleStatus) => (
        <Tag color={VehicleStatusColorCode[status]}>{VehicleStatus[status]}</Tag>
      ),
    },
    {
      title: 'Company',
      dataIndex: ['seller', 'companyName'],
      key: 'seller',
    },
    {
      title: 'Seller Name',
      dataIndex: ['seller', 'personInCharge'],
      key: 'seller',
    },
  ];

  const SortableItem = SortableElement((props: React.HTMLAttributes<HTMLTableRowElement>) => (
    <tr {...props} />
  ));
  const SortableBody = SortableContainer((props: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <tbody {...props} />
  ));

  useEffect(() => {
    if (auction) {
      const auctionItems = [...auction.auctionItems];
      auctionItems.sort((a, b) => a.lotNumber - b.lotNumber);
      setVehicles(auctionItems.map((item) => item.vehicle));
    }
  }, [auction]);

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onSortEnd = ({ oldIndex, newIndex }: SortEnd) => {
    if (!auction || !vehicles) return;

    if (oldIndex !== newIndex) {
      const data = arrayMoveImmutable(vehicles.slice(), oldIndex, newIndex).filter(
        (el: Vehicle) => !!el,
      );

      updateAuctionVehicles({
        auctionId: auction.id,
        vehicleList: data.map((vehicle, index) => ({
          vehicleId: vehicle.id,
          lotNumber: index + 1,
        })),
      })
        .unwrap()
        .then(() => {
          setVehicles(data);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const handleAddVehicles = (): void => {
    setDrawerVisible(true);
  };

  const handleEditAuction = (): void => {
    setEditDrawerVisible(true);
  };

  const handleCancelAuction = (): void => {
    Modal.confirm({
      title: 'Delete Auction',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to cancel this auction?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        if (auction)
          cancelAuction(auction?.id)
            .unwrap()
            .then(() => {
              notification.success({
                message: 'Success',
                description: 'Auction deleted successfully',
              });
            })
            .catch((error) => {
              console.log(error);
            });
      },
    });
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const DraggableContainer = (props: SortableContainerProps) => (
    <SortableBody useDragHandle helperClass='row-dragging' onSortEnd={onSortEnd} {...props} />
  );

  const DraggableBodyRow: React.FC<any> = ({ className, style, ...restProps }) => {
    if (!vehicles) return <SortableItem {...restProps} />;

    // function findIndex base on Table rowKey props and should always be a right array index
    const index = vehicles.findIndex((x) => x.id === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  };

  if (isLoading) {
    return <Spinner />;
  }

  const handleUpdateAuction = (): void => {
    void (async () => {
      if (auctionDateTime) {
        await updateAuction(auctionDateTime?.toDate())
          .unwrap()
          .then(() => {
            notification.success({
              message: 'Update Auction',
              description: 'Auction updated successfully',
            });

            setEditDrawerVisible(false);
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        notification.error({
          message: 'Update Auction',
          description: 'Please select a valid date and time',
        });
      }
    })();
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    // Can not select days before today and today
    return current && current < moment().endOf('day');
  };

  return (
    <div className='site-page-header-ghost-wrapper'>
      {!auction && <CreateAuction />}
      {auction && (
        <>
          <PageHeader
            ghost={false}
            title={auction.title}
            subTitle={<Tag color='red'>{auction.status.status}</Tag>}
            extra={[
              <Button key='edit' type='primary' ghost onClick={handleEditAuction}>
                Edit Auction
              </Button>,
              <Button
                key='cancel'
                type='primary'
                ghost
                danger
                onClick={handleCancelAuction}
                loading={isCancelAuctionLoading}
              >
                Delete Auction
              </Button>,
            ]}
          ></PageHeader>
          <Descriptions size='small' column={1} labelStyle={{ fontWeight: 'bold' }}>
            <Descriptions.Item label='Auction Date'>
              {formatDate(auction.auctionDate.toString())}
            </Descriptions.Item>
            <Descriptions.Item label='Auction Time'>
              {formatTime(auction.auctionDate.toString())}
            </Descriptions.Item>
          </Descriptions>
          <Divider />
          <Row>
            <Typography.Title level={4}>Available Vehicles</Typography.Title>
            {auction?.status.id === 1 && (
              <Button
                key='manage'
                type='primary'
                onClick={handleAddVehicles}
                style={{ marginLeft: 'auto' }}
              >
                Add/Remove Vehicles
              </Button>
            )}
          </Row>
          <br />
          <Table
            sortDirections={['descend', 'ascend']}
            dataSource={vehicles}
            columns={columns}
            rowKey='id'
            size='small'
            pagination={false}
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  navigate(`/vehicles/${record.id}`);
                },
              };
            }}
            components={{
              body: {
                wrapper: DraggableContainer,
                row: DraggableBodyRow,
              },
            }}
            loading={isUpdatingVehicles}
          />
          <Drawer
            title='Add/Remove Vehicles'
            width={800}
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            bodyStyle={{ paddingBottom: 80 }}
          >
            <AddVehicles
              auctionId={auction?.id}
              addedVehicles={vehicles}
              onSaveSuccess={() => setDrawerVisible(false)}
            />
          </Drawer>
          <Drawer
            title='Auction Date/Time'
            width={300}
            onClose={() => setEditDrawerVisible(false)}
            open={editDrawerVisible}
            bodyStyle={{ paddingBottom: 80 }}
          >
            <Space direction='vertical'>
              Auction Date/Time
              <DatePicker
                disabledDate={disabledDate}
                showTime={{ defaultValue: moment('12:00:00', 'HH:mm:ss') }}
                format='YYYY-MM-DD HH:mm'
                onOk={(value) => {
                  setAuctionDateTime(value);
                }}
              />
              <Button type='primary' onClick={handleUpdateAuction} loading={isUpdateAuctionLoading}>
                Change Date
              </Button>
            </Space>
          </Drawer>
        </>
      )}
    </div>
  );
};

export default NextAuction;
